import { Injectable, signal } from '@angular/core';
import { Skin } from '@core/interfaces/skin.interface';
import {
  WIN_TOAST_MAX_STACK,
  WIN_TOAST_SEEN_KEYS_MAX,
  WinToastGame,
} from '@core/constants/win-toast.constants';

export interface WinToastInput {
  game: WinToastGame;
  gameId: number | string;
  /** King rounds only. */
  roundId?: number;
  /** Jackpot only. */
  potid?: number;
  skins: Skin[];
  totalValue: number;
  /** Steam trade offer URL. Required — pushes without it are dropped by the port. */
  offerUrl: string;
  /** Steam trade offer id, used for deduplication when present. */
  offerid?: string;
  /** Raised by the dev launcher: do not emit any real BE event. */
  devMock?: boolean;
}

export interface WinToast extends WinToastInput {
  id: string;
  createdAt: number;
}

/** The only toast id builder, so number/string game ids cannot drift. */
export function winToastId(input: WinToastInput): string {
  return input.offerid
    ? `offer:${input.offerid}`
    : `${input.game}:${String(input.gameId)}:${input.roundId ?? input.potid ?? ''}`;
}

@Injectable({
  providedIn: 'root',
})
export class WinToastService {
  private readonly toasts = signal<WinToast[]>([]);
  readonly items = this.toasts.asReadonly();

  private readonly seenKeys = new Set<string>();

  show(input: WinToastInput): boolean {
    const id = winToastId(input);
    if (this.seenKeys.has(id)) return false;

    this.seenKeys.add(id);
    while (this.seenKeys.size > WIN_TOAST_SEEN_KEYS_MAX) {
      const oldest = this.seenKeys.values().next().value as string;
      this.seenKeys.delete(oldest);
    }

    const toast: WinToast = { ...input, id, createdAt: Date.now() };
    this.toasts.update((list) => [toast, ...list].slice(0, WIN_TOAST_MAX_STACK));
    return true;
  }

  dismiss(id: string): void {
    this.toasts.update((list) => list.filter((t) => t.id !== id));
  }
}
